import { ONBOARDING_IMAGES } from 'core/assets';
import MobileNumberInput from './MobileNumberInput/MobileNumberInput';
import { Button, VARIANTS } from '@groww-tech-pvt/ui-toolkit';
import { ROUTES } from 'src/utils/constants/routes';
import { JOURNEY_TYPES } from 'core/constants';
import { useNavigateToRoute } from 'core/hooks/useOnboardingRouter';
import { useEffect, useState } from 'react';
import { trackEvent } from 'src/utils/adapter/gobbler';
import { CompleteTxnDetailIcon } from '@groww-tech-pvt/icon-store/custom';
import { GobblerNOFWebOnbApiError, GobblerNOFWebOnbCtaClick, GobblerNOFWebOnbPageView, GobblerNOFWebOnbSuccessState } from '@groww-tech-pvt/gobbler-proto-ts/dist';

import OnbImage from 'src/components/Image';
import ActionFooter from 'src/components/ActionFooter';
import ScreenConfig from 'src/components/ScreenConfig';
import { useHoistValue } from 'src/utils/adapter/hoist';
import { MOBILE_LAYOUT_MAX_WIDTH_PX, DIGIO_HUF_LINK, APP_DOWNLOAD_URL } from 'src/utils/constants';
import { useAsyncCallback } from 'react-async-hook';
import { onbToast } from 'src/components/Toast';
import { activateJourney } from 'src/utils/services';
import OnbLoader from 'src/components/OnbLoader';


const SCREEN_TYPES = {
  MOBILE_NUMBER_INPUT: 'MOBILE_NUMBER_INPUT',
  SUCCESS_SCREEN: 'SUCCESS_SCREEN',
};


const AppQr = () => {
  const [screen, setScreen] = useState(SCREEN_TYPES.MOBILE_NUMBER_INPUT);
  const [mobileNumber, setMobileNumber] = useState('');
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const { hoistValue: isHUFOnboardingEnabled, isHoistLoading: isHufOnboardingHoistLoading } = useHoistValue({
    hoistKey: 'web-huf-onboarding',
    defaultValue: false
  });

  const getActiveJourneyApi = useAsyncCallback(activateJourney, {
    onSuccess: (data) => {
      const activeJourney = data?.data?.status !== 'AVAILABLE';
      const journeyType = data?.data?.journeyType;
      if (activeJourney) {
        if (journeyType === JOURNEY_TYPES.HUF_ONBOARDING && isHUFOnboardingEnabled && !isHufOnboardingHoistLoading) {
          navigateToRoute(ROUTES.ONBOARDING, { journeyType: JOURNEY_TYPES.HUF_ONBOARDING });
        }
      }
      trackEvent('GobblerNOFWebOnbSuccessState', GobblerNOFWebOnbSuccessState, { state: 'active-journey-activated' });
    },
    onError: () => {
      trackEvent('GobblerNOFWebOnbApiError', GobblerNOFWebOnbApiError, { errorMessage: 'active-journey-activation-failed' });
      onbToast.error({ description: 'Something went wrong.' });
    }
  });

  const title = 'Groww lets you invest as an individual or an HUF';
  const { navigateToRoute } = useNavigateToRoute();

  useEffect(() => {
    trackEvent('GobblerNOFWebOnbPageView', GobblerNOFWebOnbPageView, {
      dataType: 'AppQrScreen',
    });
    if (!isHufOnboardingHoistLoading && isHUFOnboardingEnabled) getActiveJourneyApi.execute();
  }, [isHufOnboardingHoistLoading])

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_LAYOUT_MAX_WIDTH_PX}px)`);
    const sync = () => setIsMobileViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const handleRedirectionToHuf = () => {
    if (isHUFOnboardingEnabled) {
      trackEvent('GobblerNOFWebOnbCtaClick', GobblerNOFWebOnbCtaClick, {
        buttonText: 'Open HUF account',
        buttonAction: 'redirection-to-huf-from-qr-screen-through-web-routing',
      });
      navigateToRoute(ROUTES.ONBOARDING, { journeyType: JOURNEY_TYPES.HUF_ONBOARDING });
    } else {
      trackEvent('GobblerNOFWebOnbCtaClick', GobblerNOFWebOnbCtaClick, {
        buttonText: 'Open HUF account',
        buttonAction: 'redirection-to-huf-from-qr-screen-through-digio',
      });
      window.location.href = DIGIO_HUF_LINK;
    }
  };

  const onSuccess = () => {
    trackEvent('GobblerNOFWebOnbSuccessState', GobblerNOFWebOnbSuccessState, { state: 'sms_sent_successfully' });
    setScreen(SCREEN_TYPES.SUCCESS_SCREEN);
  };

  const getMobileLinkUi = () => {
    switch (screen) {
      case SCREEN_TYPES.MOBILE_NUMBER_INPUT:
        return (
          <MobileNumberInput
            mobileNumber={mobileNumber}
            setMobileNumber={setMobileNumber}
            onSuccess={onSuccess}
          />
        );

      case SCREEN_TYPES.SUCCESS_SCREEN:
        return (
          <div className='adp901SuccessScreen absolute-center'>
            <CompleteTxnDetailIcon
              size={80}
              className='adp901SuccessIcon'
              data-test-id='onboarding-app-download-sms-success-icon'
            />
            <div
              className='adp901SuccessText'
              data-test-id='onboarding-app-download-sms-success-text'
            >
              SMS sent to +91 {mobileNumber}.<br /> Check your inbox.
            </div>
          </div>
        );
    }
  };

  const getHufOnboardingUI = () => {
    return (
      <div className='flex flex-column gap-2'>
        <div className='bodyBase'>
          For opening an HUF Demat account
        </div>
        <Button
          buttonText='Click here'
          onClick={handleRedirectionToHuf}
          variant={VARIANTS.PRIMARY}
          isLoading={isHufOnboardingHoistLoading}
        />
      </div>
    );
  };

  if (isMobileViewport) {
    return (
      <div className="h-full flex flex-col min-h-0">
        <div className="headingBase mb-4">{title}</div>
        <div className="flex-1 flex flex-col items-center justify-center min-h-0 mb-4">
          <OnbImage
            src={ONBOARDING_IMAGES.DOWNLOAD_APP_MSITE}
            width="200px"
            height="auto"
            alt=""
            className="max-w-md object-contain"
          />
        </div>
        <ActionFooter
          primaryButton={{
            text: 'Download Groww app',
            onClick: () => window.location.assign(APP_DOWNLOAD_URL),
          }}
          secondaryButton={{
            text: 'Open HUF account',
            onClick: handleRedirectionToHuf,
            isLoading: isHufOnboardingHoistLoading,
          }}
        />
      </div>
    );
  }

  const getLeftSideUi = () => {
    return (
      <div className="h-full flex flex-col w-full">
        <ScreenConfig heading={title} />
        <div className='flex h-full absolute-center gap-6'>
          <div className='flex flex-column valign-wrapper'>
            <div className='mb-2 center-align bodyBase'>Scan here for individual account</div>
            <OnbImage
              src={ONBOARDING_IMAGES.APP_QR_CODE}
              width={'240px'}
              height={'auto'}
            />
          </div>
          <div className='contentSecondary'>or</div>
          <div className='flex flex-column gap-2.5'>
            {getMobileLinkUi()}
            {getHufOnboardingUI()}
          </div>

        </div>
      </div>
    );
  }


  if(isHufOnboardingHoistLoading || getActiveJourneyApi.loading) {
    return <div className="absolute-center height100">
      <OnbLoader />
    </div>;
  }

  return (
    <div className='flex h-full gap-6 w-full'>
      {getLeftSideUi()}
    </div>
  )
};

export default AppQr;
